import type * as Phaser from 'phaser';
import { getVisualPreloadSummary } from '../data/assets/visualAssets';
import { Logger, createLogger } from './Logger';

export interface TextureFallbackSummary {
  readonly generatedKeys: readonly string[];
  readonly skippedExistingKeys: readonly string[];
  readonly unknownKeys: readonly string[];
}

interface FallbackTextureDefinition {
  readonly key: string;
  readonly width: number;
  readonly height: number;
  readonly draw: (graphics: Phaser.GameObjects.Graphics) => void;
}

export const FALLBACK_TEXTURE_KEYS = Object.freeze([
  'player',
  'enemy',
  'bullet',
  'orbit_orb',
  'weapon_area',
  'weapon_beam',
  'weapon_boomerang',
  'weapon_chain',
  'gem',
  'chest',
  'ground',
  'ground_grid'
]);

export function ensureFallbackTextures(
  scene: Phaser.Scene,
  keys: readonly string[] = FALLBACK_TEXTURE_KEYS,
  logger: Logger = createLogger('TextureFallbacks')
): TextureFallbackSummary {
  const generatedKeys: string[] = [];
  const skippedExistingKeys: string[] = [];
  const unknownKeys: string[] = [];

  for (const key of keys) {
    const definition = FALLBACK_TEXTURE_DEFINITIONS[key];
    if (!definition) {
      logger.warn('ensureFallbackTextures:unknown-key', { key });
      unknownKeys.push(key);
      continue;
    }

    if (scene.textures.exists(key)) {
      logger.debug('ensureFallbackTextures:skip-existing', { key });
      skippedExistingKeys.push(key);
      continue;
    }

    const graphics = scene.make.graphics({ x: 0, y: 0 });
    definition.draw(graphics);
    graphics.generateTexture(definition.key, definition.width, definition.height);
    graphics.destroy();
    logger.info('ensureFallbackTextures:generated', {
      key,
      width: definition.width,
      height: definition.height,
      reason: 'texture-missing'
    });
    generatedKeys.push(key);
  }

  return { generatedKeys, skippedExistingKeys, unknownKeys };
}

export function getVisualDiagnosticsSummary(fallbackSummary: TextureFallbackSummary | null): {
  manifestKeys: readonly string[];
  loadedKeys: readonly string[];
  fallbackKeys: readonly string[];
  skippedOptionalKeys: readonly string[];
  failedKeys: readonly string[];
  requiredFailures: readonly string[];
} {
  const preloadSummary = getVisualPreloadSummary();
  return {
    manifestKeys: preloadSummary.manifestKeys,
    loadedKeys: preloadSummary.loadedKeys,
    fallbackKeys: fallbackSummary?.generatedKeys ?? [],
    skippedOptionalKeys: preloadSummary.skippedOptionalKeys,
    failedKeys: preloadSummary.failedKeys,
    requiredFailures: preloadSummary.requiredFailures
  };
}

const FALLBACK_TEXTURE_DEFINITIONS: Record<string, FallbackTextureDefinition> = {
  ground: {
    key: 'ground',
    width: 512,
    height: 512,
    draw: graphics => {
      graphics.fillStyle(0xffffff);
      graphics.fillRect(0, 0, 512, 512);
    }
  },
  ground_grid: {
    key: 'ground_grid',
    width: 512,
    height: 512,
    draw: graphics => {
      graphics.lineStyle(2, 0xffffff, 1);
      graphics.strokeRect(0, 0, 512, 512);
      graphics.beginPath();
      graphics.moveTo(0, 0);
      graphics.lineTo(512, 512);
      graphics.strokePath();
    }
  },
  player: {
    key: 'player',
    width: 32,
    height: 32,
    draw: graphics => {
      graphics.fillStyle(0x3366ff);
      graphics.fillRect(0, 0, 32, 32);
    }
  },
  enemy: {
    key: 'enemy',
    width: 32,
    height: 32,
    draw: graphics => {
      graphics.fillStyle(0xffffff);
      graphics.fillCircle(16, 16, 16);
    }
  },
  bullet: {
    key: 'bullet',
    width: 8,
    height: 8,
    draw: graphics => {
      graphics.fillStyle(0xffff00);
      graphics.fillRect(0, 0, 8, 8);
    }
  },
  orbit_orb: {
    key: 'orbit_orb',
    width: 16,
    height: 16,
    draw: graphics => {
      graphics.fillStyle(0x00ffff);
      graphics.fillCircle(8, 8, 8);
    }
  },
  weapon_area: {
    key: 'weapon_area',
    width: 24,
    height: 24,
    draw: graphics => {
      graphics.lineStyle(2, 0xff8844, 1);
      const withCircle = graphics as Phaser.GameObjects.Graphics & { strokeCircle?: (x: number, y: number, radius: number) => void };
      if (typeof withCircle.strokeCircle === 'function') {
        withCircle.strokeCircle(12, 12, 10);
      }
    }
  },
  weapon_beam: {
    key: 'weapon_beam',
    width: 32,
    height: 8,
    draw: graphics => {
      graphics.fillStyle(0x88ccff);
      graphics.fillRect(0, 0, 32, 8);
    }
  },
  weapon_boomerang: {
    key: 'weapon_boomerang',
    width: 24,
    height: 12,
    draw: graphics => {
      graphics.fillStyle(0x99ffcc);
      graphics.fillRect(0, 2, 24, 8);
    }
  },
  weapon_chain: {
    key: 'weapon_chain',
    width: 12,
    height: 12,
    draw: graphics => {
      graphics.fillStyle(0xffff99);
      graphics.fillCircle(6, 6, 6);
    }
  },
  gem: {
    key: 'gem',
    width: 10,
    height: 10,
    draw: graphics => {
      graphics.fillStyle(0x00ff00);
      graphics.fillCircle(5, 5, 5);
    }
  },
  chest: {
    key: 'chest',
    width: 22,
    height: 18,
    draw: graphics => {
      graphics.fillStyle(0xc0822d);
      graphics.fillRect(2, 6, 18, 10);
      graphics.fillStyle(0xf0c36a);
      graphics.fillRect(4, 3, 14, 5);
      graphics.fillStyle(0x3b2514);
      graphics.fillRect(10, 8, 3, 5);
    }
  }
};
